import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { withTransientRetry } from './transientReadRetry';

/**
 * The dashboard's verification-state check, extracted from UserDashboard so
 * its stale-result safety is unit-testable (verificationCheck.test.js).
 *
 * Stale-result barrier (2026-09-29 review finding): this check captures the
 * user at start and its Firestore branch reads users/{capturedUid}. Without
 * a barrier, a slow run for user A resolving AFTER a switch to user B would
 * set B's dashboard state from A's document — and the transient retry widens
 * that in-flight window by up to ~1s. So the signed-in uid is re-checked
 * after EVERY await, and a result whose uid no longer matches is discarded
 * without touching state. (The caller's single-flight guard prevents
 * duplicate concurrent starts for the same uid; it cannot prevent staleness
 * across DIFFERENT uids — that is this barrier's job.)
 *
 * Read-only; errors propagate to the caller (UserDashboard keeps its
 * existing keep-current-state catch).
 *
 * @param {object} user          the Firebase user captured at start
 * @param {Function} setIsVerified  state setter; only invoked while the
 *                                  captured uid is still the signed-in uid
 */
export async function runVerificationCheck(user, setIsVerified) {
  const uid = user.uid;
  const stillCurrent = () => auth.currentUser?.uid === uid;

  // user.reload() is a network call — offline it rejects with
  // auth/network-request-failed, which used to escape uncaught and crash
  // the dashboard. Read-only and idempotent, so a transient blip gets one
  // retry (web only — the helper is a pass-through on native); both
  // attempts failing propagates to the caller's keep-current-state catch
  // (this exact path fired in the 2026-09-29 incident).
  await withTransientRetry('verification-check', async () => {
    await user.reload();
    if (!stillCurrent()) return; // uid changed mid-flight: stale, discard
    if (auth.currentUser.emailVerified) {
      setIsVerified(true);
      return;
    }
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (!stillCurrent()) return; // re-check: the getDoc awaited too
    if (userDoc.exists()) {
      setIsVerified(userDoc.data().isVerified ?? false);
    }
  });
}
